// Browser-safe XLSX download.
//
// SheetJS's `XLSX.writeFile` writes via Node's `fs`, which Vite externalizes for
// the browser — calling it throws "Module 'fs' has been externalized … Cannot
// access 'fs.writeFileSync' in client code" and the download silently fails.
// Serialize to an array buffer and download via Blob instead (same approach the
// ZIP bundles already use with `XLSX.write({ type: "array" })`).

import * as XLSX from "xlsx-js-style";
import { triggerBlobDownload } from "./_helpers";

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Excel's hard limit on the character length of a single cell. SheetJS throws
// "Text length must not exceed 32767 characters" at write time if any cell
// exceeds it — a portfolio-scale export can hit this with a long survey answer,
// notes field, or joined evidence-URL list. Clamp rather than crash the export.
const EXCEL_MAX_CELL_TEXT = 32767;

/** Truncate any over-long string cell so SheetJS can serialize the workbook. */
function clampCellText(workbook: XLSX.WorkBook): void {
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) continue;
    for (const ref of Object.keys(sheet)) {
      if (ref[0] === "!") continue; // skip metadata keys (!ref, !cols, …)
      const cell = (sheet as XLSX.Sheet)[ref] as XLSX.CellObject;
      if (cell && typeof cell.v === "string" && cell.v.length > EXCEL_MAX_CELL_TEXT) {
        console.warn(
          `[export] Cell ${sheetName}!${ref} exceeds Excel's ${EXCEL_MAX_CELL_TEXT}-char limit (${cell.v.length}); truncating.`,
        );
        cell.v = `${cell.v.slice(0, EXCEL_MAX_CELL_TEXT - 1)}…`;
        if (typeof cell.w === "string") cell.w = cell.v as string;
      }
    }
  }
}

export function downloadWorkbook(workbook: XLSX.WorkBook, filename: string): void {
  clampCellText(workbook);
  const buf = XLSX.write(workbook, { type: "array", bookType: "xlsx" }) as ArrayBuffer;
  triggerBlobDownload(new Blob([buf], { type: XLSX_MIME }), filename);
}
